// Slice 43 — Customers (CRM-lite). List + Create-modal + Detail-modal.
//
// Email is ALWAYS shown in the masked form (`k****@l***.no`); the only way
// an operator sees the raw email is through the audited Reveal-modal
// inside the detail view (spec §5.3 — the audit is the gate, not a
// follow-up). Tombstoned customers (Art. 17 erased) are listed with a
// `tombstoned`-badge so staff don't try to mutate them.

import { useCallback, useEffect, useState } from "react";
import { type CustomerRow, CustomersApi, type LawfulBasis } from "../../api/client";
import { useRouter } from "../../router/router";
import { useShell } from "../../shell/HqShellContext";
import { HqAvatar, HqBadge, HqButton, HqCard, HqInput, HqStatusDot, Icons } from "../../ui";
import { CreateCustomerModal } from "./CreateCustomerModal";

export function CustomersScreen() {
  const { refreshTick, bumpRefresh, toast } = useShell();
  const { navigate } = useRouter();
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [tagSummary, setTagSummary] = useState<{ tag: string; count: number }[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    void refreshTick;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void (async () => {
      const [listRes, tagsRes] = await Promise.all([
        CustomersApi.list({
          limit: 50,
          q: q.trim() || undefined,
          tag: tag || undefined,
          country: country || undefined,
        }),
        CustomersApi.tagsSummary(),
      ]);
      if (cancelled) return;
      setLoading(false);
      if (!listRes.ok) {
        setErr(listRes.error.message);
        return;
      }
      setRows(listRes.data.data);
      setNextCursor(listRes.data.page.cursor);
      if (tagsRes.ok) setTagSummary(tagsRes.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [q, tag, country, refreshTick]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    const res = await CustomersApi.list({
      limit: 50,
      cursor: nextCursor,
      q: q.trim() || undefined,
      tag: tag || undefined,
      country: country || undefined,
    });
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    setRows((prev) => [...prev, ...res.data.data]);
    setNextCursor(res.data.page.cursor);
  }, [nextCursor, q, tag, country, toast]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Header total={rows.length} onCreate={() => setCreateOpen(true)} />
      <KpiStrip rows={rows} totalTags={tagSummary.length} />
      <FilterBar
        q={q}
        onQ={setQ}
        tag={tag}
        onTag={setTag}
        country={country}
        onCountry={setCountry}
        tagOptions={tagSummary}
      />
      <HqCard padding={false}>
        <Table
          rows={rows}
          loading={loading}
          err={err}
          onOpen={(id) => navigate("customers", id)}
          onSuspend={async (id) => {
            const reason = prompt(
              "Suspend reason (lands in audit + revoked_reason)?",
              "non-payment",
            );
            if (!reason) return;
            const res = await CustomersApi.suspend(id, reason.trim());
            if (!res.ok) {
              toast(res.error.message, "danger");
              return;
            }
            toast(`Suspended ${res.data.suspended} license(s).`, "warn");
            bumpRefresh();
          }}
        />
        {nextCursor && (
          <div style={{ padding: 12, display: "flex", justifyContent: "center" }}>
            <HqButton variant="secondary" size="sm" onClick={() => void loadMore()}>
              Load more
            </HqButton>
          </div>
        )}
      </HqCard>
      <CreateCustomerModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setCreateOpen(false);
          bumpRefresh();
          toast("Customer created.", "success");
          if (id) navigate("customers", id);
        }}
      />
    </div>
  );
}

function KpiStrip({ rows, totalTags }: { rows: CustomerRow[]; totalTags: number }) {
  const total = rows.length;
  const eea = rows.filter((r) => r.isEea && r.tombstonedAt == null).length;
  const tombstoned = rows.filter((r) => r.tombstonedAt != null).length;
  const feedbackOff = rows.filter((r) => !r.feedbackEnabled).length;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
      <Kpi label="On page" value={total.toString()} hint="loaded rows" />
      <Kpi label="EEA" value={eea.toString()} hint="active + EEA" tone="ok" />
      <Kpi
        label="Tombstoned"
        value={tombstoned.toString()}
        hint="Art. 17 erased"
        {...(tombstoned > 0 ? { tone: "warn" as const } : {})}
      />
      <Kpi
        label="Distinct tags"
        value={totalTags.toString()}
        hint={`${feedbackOff} feedback off`}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "ok" | "warn" | "danger";
}) {
  const color =
    tone === "warn" ? "var(--hq-warn)" : tone === "danger" ? "var(--hq-danger)" : "var(--hq-text)";
  return (
    <div
      style={{
        padding: 12,
        background: "var(--hq-bg-input)",
        border: "1px solid var(--hq-border-input)",
        borderRadius: "var(--hq-radius-md)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <span
        className="hq-mono"
        style={{
          fontSize: 9.5,
          color: "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 18, fontWeight: 600, color }}>{value}</span>
      {hint && (
        <span className="hq-mono" style={{ fontSize: 10, color: "var(--hq-text-dim)" }}>
          {hint}
        </span>
      )}
    </div>
  );
}

function FilterBar({
  q,
  onQ,
  tag,
  onTag,
  country,
  onCountry,
  tagOptions,
}: {
  q: string;
  onQ: (v: string) => void;
  tag: string;
  onTag: (v: string) => void;
  country: string;
  onCountry: (v: string) => void;
  tagOptions: { tag: string; count: number }[];
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <div style={{ flex: 1, maxWidth: 340 }}>
        <HqInput
          placeholder="Search masked email, name, or customer ID…"
          leadingIcon={Icons.Search}
          value={q}
          onChange={(e) => onQ(e.target.value)}
        />
      </div>
      <select value={tag} onChange={(e) => onTag(e.target.value)} style={selectStyle()}>
        <option value="">All tags</option>
        {tagOptions.map((t) => (
          <option key={t.tag} value={t.tag}>
            {t.tag} ({t.count})
          </option>
        ))}
      </select>
      <input
        value={country}
        onChange={(e) => onCountry(e.target.value.toUpperCase().slice(0, 2))}
        placeholder="ISO-2"
        style={{ ...selectStyle(), width: 80, fontFamily: "var(--hq-font-mono)" }}
      />
      {(tag || country || q) && (
        <HqButton
          variant="ghost"
          size="sm"
          onClick={() => {
            onQ("");
            onTag("");
            onCountry("");
          }}
        >
          Clear filters
        </HqButton>
      )}
    </div>
  );
}

function selectStyle(): React.CSSProperties {
  return {
    height: 30,
    padding: "0 10px",
    background: "var(--hq-bg-input)",
    border: "1px solid var(--hq-border-input)",
    color: "var(--hq-text)",
    borderRadius: "var(--hq-radius-md)",
    fontSize: 12,
    outline: "none",
  };
}

function Header({ total, onCreate }: { total: number; onCreate: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Icons.Users width={20} height={20} sw={1.7} style={{ color: "var(--hq-accent)" }} />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: "-.02em",
            color: "var(--hq-text)",
            lineHeight: 1.2,
          }}
        >
          Customers
        </h1>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
          {total} on record
        </span>
      </div>
      <span style={{ flex: 1 }} />
      <HqButton variant="primary" leadingIcon={Icons.Plus} onClick={onCreate}>
        New customer
      </HqButton>
    </div>
  );
}

function Table({
  rows,
  loading,
  err,
  onOpen,
  onSuspend,
}: {
  rows: CustomerRow[];
  loading: boolean;
  err: string | null;
  onOpen: (id: string) => void;
  onSuspend: (id: string) => void;
}) {
  if (err) return <Empty message={err} tone="danger" />;
  if (rows.length === 0 && loading) return <Empty message="Loading…" tone="dim" />;
  if (rows.length === 0)
    return <Empty message="No customers match the current filters." tone="dim" />;
  return (
    <div style={{ maxHeight: 600, overflow: "auto" }} className="hq-scroll">
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <Th>
            <Cell head>Customer</Cell>
            <Cell head>Basis</Cell>
            <Cell head>Country</Cell>
            <Cell head>Joined</Cell>
            <Cell head>Status</Cell>
            <Cell head right />
          </Th>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Row key={r.id} row={r} onOpen={() => onOpen(r.id)} onSuspend={() => onSuspend(r.id)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <tr
      style={{
        position: "sticky",
        top: 0,
        background: "var(--hq-bg-raised)",
        borderBottom: "1px solid var(--hq-border)",
        textAlign: "left",
        zIndex: 1,
      }}
    >
      {children}
    </tr>
  );
}

function Cell({
  children,
  head,
  right,
}: {
  children?: React.ReactNode;
  head?: boolean;
  right?: boolean;
}) {
  return (
    <td
      style={{
        padding: head ? "10px 14px" : "12px 14px",
        textAlign: right ? "right" : "left",
        color: head ? "var(--hq-text-dim)" : "var(--hq-text)",
        fontFamily: head ? "var(--hq-font-mono)" : "var(--hq-font-sans)",
        fontSize: head ? 10.5 : 12,
        letterSpacing: head ? ".1em" : 0,
        textTransform: head ? "uppercase" : "none",
        fontWeight: head ? 500 : 400,
        verticalAlign: "middle",
      }}
    >
      {children}
    </td>
  );
}

const BASIS_LABEL: Record<LawfulBasis, string> = {
  contract: "Contract",
  consent: "Consent",
  legitimate_interest: "Legit. interest",
};

function Row({
  row,
  onOpen,
  onSuspend,
}: {
  row: CustomerRow;
  onOpen: () => void;
  onSuspend: () => void;
}) {
  const tombstoned = row.tombstonedAt != null;
  return (
    <tr
      style={{
        borderBottom: "1px solid var(--hq-border)",
        cursor: "pointer",
      }}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      tabIndex={0}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLTableRowElement).style.background = "var(--hq-bg-hover)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLTableRowElement).style.background = "transparent";
      }}
    >
      <Cell>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <HqAvatar name={row.displayName ?? row.emailMasked} size={26} />
          <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 500,
                color: tombstoned ? "var(--hq-text-dim)" : "var(--hq-text)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 240,
                textDecoration: tombstoned ? "line-through" : "none",
              }}
            >
              {row.displayName ?? "—"}
            </span>
            <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
              {row.emailMasked}
            </span>
          </span>
        </span>
      </Cell>
      <Cell>
        <HqBadge tone={row.lawfulBasis === "contract" ? "accent" : "neutral"}>
          {BASIS_LABEL[row.lawfulBasis]}
        </HqBadge>
      </Cell>
      <Cell>
        <span style={{ fontFamily: "var(--hq-font-mono)", fontSize: 11 }}>
          {row.country ?? "—"}{" "}
          {row.isEea && (
            <HqBadge tone="success" size="sm">
              EEA
            </HqBadge>
          )}
        </span>
      </Cell>
      <Cell>
        <span
          style={{ fontFamily: "var(--hq-font-mono)", color: "var(--hq-text-muted)", fontSize: 11 }}
        >
          {fmtDate(row.createdAt)}
        </span>
      </Cell>
      <Cell>
        {tombstoned ? (
          <HqBadge tone="warn">tombstoned</HqBadge>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <HqStatusDot tone="success" size={6} />
            <span style={{ fontSize: 11.5, color: "var(--hq-text-muted)" }}>active</span>
          </span>
        )}
      </Cell>
      <Cell right>
        <span
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          style={{ display: "inline-flex", gap: 6, alignItems: "center" }}
        >
          {!tombstoned && (
            <HqButton
              variant="ghost"
              size="sm"
              leadingIcon={Icons.Revoke}
              onClick={onSuspend}
              title="Suspend all licenses"
            >
              Suspend
            </HqButton>
          )}
          <Icons.ChevD
            width={12}
            height={12}
            sw={1.7}
            style={{ color: "var(--hq-text-dim)", transform: "rotate(-90deg)" }}
          />
        </span>
      </Cell>
    </tr>
  );
}

function Empty({ message, tone }: { message: string; tone: "dim" | "danger" }) {
  return (
    <div
      style={{
        padding: 48,
        textAlign: "center",
        color: tone === "danger" ? "var(--hq-danger)" : "var(--hq-text-dim)",
        fontSize: 13,
      }}
    >
      {message}
    </div>
  );
}

function fmtDate(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
